/* eslint-disable @typescript-eslint/no-explicit-any */
import { Trans } from "@lingui/react/macro";
import dayjs from "dayjs";
import MonthDayBlock from "../../../../../Components/NextBase/MonthDayBlock";
import { useCalendarStore } from "../../store/useCalendarStore";
import MonthAppointmentBlock from "./MonthAppointmentBlock";
import { capitalize } from "lodash";
import { Button, Popover, PopoverContent, PopoverTrigger } from "@heroui/react";
import { useMemo } from "react";
import {
  isDayFullyAbsent,
  type MonthTimedTimeOff,
  monthTimeOffsForDay,
} from "./monthTimeOffs";
import { MonthAbsenceBadge, MonthTimeOffBlock } from "./MonthTimeOffBlock";

const MAX_POSITIONS = 3; // Maximum number of positions for appointments before showing "more"

type DayItem =
  | { kind: "appointment"; appointment: any; start: dayjs.Dayjs }
  | { kind: "timeOff"; entry: MonthTimedTimeOff; start: dayjs.Dayjs };

type PositionedItem = {
  item: DayItem;
  position: number;
  isVisible: boolean;
};

const MonthCalendar = () => {
  const {
    calendarData,
    dateActions,
    filterItems,
    selectedDate,
    showOnlyFixedAppointments,
    timeoffs,
  } = useCalendarStore((state) => state);

  const { allAppointments } = calendarData;

  const displayedMonth = selectedDate ?? dayjs();

  const numDays = filterItems?.days?.length ?? 0;

  // Split multi-day appointments into segments by week
  const multiDaySegments = useMemo(() => {
    const segments: {
      appointment: any;
      blockStart: dayjs.Dayjs;
      numDays: number;
      position?: number; // Position in the grid (1-4)
    }[] = [];

    // Filter appointments for service/user/resource filters
    const filteredAppointments = allAppointments.filter((appointment: any) => {
      const isShownService = filterItems.services.includes(
        appointment.serviceId,
      );
      const isShownUser = filterItems.users.some((u: any) =>
        appointment.userIds.includes(u),
      );
      const isShownResource = filterItems.resources.some((r: any) =>
        appointment.resources.includes(r),
      );
      const isShownFixed =
        !showOnlyFixedAppointments || appointment.fixedAppointmentId != null;
      return isShownService && (isShownUser || isShownResource) && isShownFixed;
    });

    // Create segments for multi-day appointments
    filteredAppointments.forEach((appointment: any) => {
      // compute calendar-day span
      const startDay = dayjs(appointment.startTimeUtc).startOf("day");
      const endDay = dayjs(appointment.startTimeUtc)
        .add(appointment.duration, "minutes")
        .startOf("day");
      const daysSpan = endDay.diff(startDay, "day");
      if (daysSpan <= 0) return; // skip single-day

      let segmentStart = startDay;
      const weekDowStart = filterItems.days[0];
      const daysInWeek = filterItems.days.length;

      // carve into week-based segments
      while (
        segmentStart.isBefore(endDay) ||
        segmentStart.isSame(endDay, "day")
      ) {
        const diffToWeekStart = (segmentStart.day() + 6 - weekDowStart) % 7;
        const weekStart = segmentStart.subtract(diffToWeekStart, "day");
        const weekEnd = weekStart.add(daysInWeek - 1, "day");
        const segmentEnd = endDay.isBefore(weekEnd) ? endDay : weekEnd;
        const numDaysSpan = segmentEnd.diff(segmentStart, "day") + 1;

        segments.push({
          appointment,
          blockStart: segmentStart,
          numDays: numDaysSpan,
        });

        // next slice begins the day after this segment ends
        segmentStart = segmentEnd.add(1, "day");
      }
    });

    return segments;
  }, [
    allAppointments,
    filterItems.services,
    filterItems.users,
    filterItems.resources,
    filterItems.days,
    showOnlyFixedAppointments,
  ]);

  // Create a 2D table for multi-day appointment positions - completely reset and rebuild
  const positionedSegments = useMemo(() => {
    // Group segments by week
    const weekGroups: Record<string, typeof multiDaySegments> = {};

    multiDaySegments.forEach((segment) => {
      const { blockStart } = segment;
      const weekDowStart = filterItems.days[0];
      const diffToWeekStart = (blockStart.day() + 7 - weekDowStart) % 7;
      const weekStart = blockStart.subtract(diffToWeekStart, "day");
      const weekKey = weekStart.format("YYYY-MM-DD");

      if (!weekGroups[weekKey]) {
        weekGroups[weekKey] = [];
      }
      weekGroups[weekKey].push(segment);
    });

    // Create a deep copy of the segments to prevent mutating the original array
    const segmentsWithPositions = [
      ...multiDaySegments.map((segment) => ({ ...segment })),
    ];

    // Process each week to assign positions
    Object.keys(weekGroups).forEach((weekKey) => {
      const weekSegments = weekGroups[weekKey];

      // Sort segments by start date (earliest first), then by duration (longest first)
      weekSegments.sort((a, b) => {
        // First sort by the appointment's original start time (not the segment start)
        const aStartTime = dayjs(a.appointment.startTimeUtc);
        const bStartTime = dayjs(b.appointment.startTimeUtc);

        if (!aStartTime.isSame(bStartTime)) {
          return aStartTime.diff(bStartTime); // Earlier appointments first
        }

        // If same start time, sort by duration (longest first)
        if (b.numDays !== a.numDays) {
          return b.numDays - a.numDays;
        }

        // If still tied, sort by segment start
        return a.blockStart.diff(b.blockStart);
      });

      // Create a position map for the week
      // [day][position] = appointmentId or undefined
      const positionMap: Record<string, boolean[]> = {};

      // Initialize position map
      const weekStart = dayjs(weekKey);
      for (let i = 0; i < numDays; i++) {
        const day = weekStart.add(i, "day");
        const dayKey = day.format("YYYY-MM-DD");
        positionMap[dayKey] = Array(MAX_POSITIONS).fill(false);
      }

      // Assign positions to segments
      weekSegments.forEach((segment) => {
        const { blockStart, numDays, appointment } = segment;

        // Check each position
        for (let pos = 0; pos < MAX_POSITIONS; pos++) {
          let canFit = true;

          // Check if this position is free for all days of the segment
          for (let d = 0; d < numDays; d++) {
            const day = blockStart.add(d, "day");
            const dayKey = day.format("YYYY-MM-DD");

            // Skip days not in our week or calendar
            if (!positionMap[dayKey]) continue;

            if (positionMap[dayKey][pos]) {
              canFit = false;
              break;
            }
          }

          if (canFit) {
            // Find the corresponding segment in our copy and update its position
            const segmentToUpdate = segmentsWithPositions.find(
              (s) =>
                s.appointment.appointmentId === appointment.appointmentId &&
                s.blockStart.isSame(blockStart, "day"),
            );

            if (segmentToUpdate) {
              segmentToUpdate.position = pos;
            }

            // Mark days as occupied
            for (let d = 0; d < numDays; d++) {
              const day = blockStart.add(d, "day");
              const dayKey = day.format("YYYY-MM-DD");

              // Skip days not in our week or calendar
              if (!positionMap[dayKey]) continue;

              positionMap[dayKey][pos] = true;
            }
            break;
          }
        }
      });
    });

    return segmentsWithPositions;
  }, [multiDaySegments, filterItems.days, numDays]);

  // Count multi-day appointments per day for calculating space
  const multiDayApptsPerDay = useMemo(() => {
    const counts: Record<string, { visible: number; overflow: number }> = {};

    positionedSegments.forEach((segment) => {
      const { blockStart, numDays, position } = segment;

      for (let d = 0; d < numDays; d++) {
        const day = blockStart.add(d, "day");
        const dayKey = day.format("YYYY-MM-DD");

        if (!counts[dayKey]) {
          counts[dayKey] = { visible: 0, overflow: 0 };
        }

        if (position !== undefined && position < MAX_POSITIONS) {
          counts[dayKey].visible++;
        } else {
          counts[dayKey].overflow++;
        }
      }
    });

    return counts;
  }, [positionedSegments]);

  // Build a comprehensive position map for the entire month
  const monthPositionMap = useMemo(() => {
    // Create a map that tracks which positions are occupied on each day
    const map: Record<string, (boolean | null)[][]> = {};

    // Initialize the map for all days in the calendar
    calendarData?.days?.forEach((day: any) => {
      const date = dayjs(day.date);
      const dayKey = date.format("YYYY-MM-DD");

      // For each day, create an array of positions with occupation status
      // Each position tracks [isOccupied, appointmentId]
      map[dayKey] = Array(MAX_POSITIONS).map(() => [false, null]);
    });

    // Mark positions occupied by multi-day appointments
    positionedSegments.forEach((segment) => {
      const { appointment, blockStart, numDays, position } = segment;
      if (position === undefined || position >= MAX_POSITIONS) return;

      for (let d = 0; d < numDays; d++) {
        const day = blockStart.add(d, "day");
        const dayKey = day.format("YYYY-MM-DD");

        if (map[dayKey] && position < MAX_POSITIONS) {
          // Mark this position as occupied by this appointment
          map[dayKey][position] = [true, appointment.appointmentId];
        }
      }
    });

    return map;
  }, [calendarData?.days, positionedSegments, calendarData]);

  return (
    <div>
      <div
        className="grid h-fit gap-0"
        style={{
          gridTemplateColumns: `repeat(${numDays}, minmax(0, 1fr))`,
        }}
      >
        {calendarData?.days
          .slice(0, filterItems?.days.length)
          .map((day: any) => {
            const dayName = capitalize(dayjs(day.date).format("dddd").at(0));

            return (
              <p
                key={day.date}
                className="py-1 text-center text-xs font-semibold"
              >
                {dayName}
              </p>
            );
          })}
      </div>
      <div
        className="relative grid gap-0"
        style={{
          gridTemplateColumns: `repeat(${numDays}, minmax(0, 1fr))`,
        }}
      >
        {calendarData?.days?.map((day: any, _index: number) => {
          const { date } = day;

          const cellDate = dayjs(date);
          const dayKey = cellDate.format("YYYY-MM-DD");
          const isOutsideMonth = !cellDate.isSame(displayedMonth, "month");
          const multiDayCounts = multiDayApptsPerDay[dayKey] || {
            visible: 0,
            overflow: 0,
          };

          // Get position map for this day, with a DEEP COPY to prevent modifications
          const dayPositionMap =
            monthPositionMap[dayKey] ||
            Array(MAX_POSITIONS)
              .fill(0)
              .map(() => [false, null]);

          // Create a simple array showing which positions are occupied
          const occupiedPositions = dayPositionMap.map((pos) => pos[0]);

          // filter services and users for single-day appointments
          const filteredAppointments = allAppointments.filter(
            (appointment: any) => {
              const start = dayjs(appointment.startTimeUtc).startOf("day");
              const end = start
                .add(appointment.duration, "minutes")
                .startOf("day");
              const isShownService = filterItems.services.includes(
                appointment.serviceId,
              );

              const isShownUser = filterItems.users.some((i: any) =>
                appointment.userIds.includes(i),
              );

              const isShownResource = filterItems.resources.some((i: any) =>
                appointment.resources.includes(i),
              );

              if (!isShownService || !(isShownUser || isShownResource))
                return false;
              if (
                showOnlyFixedAppointments &&
                appointment.fixedAppointmentId == null
              )
                return false;
              if (end.isBefore(cellDate) || start.isAfter(cellDate))
                return false;
              return true;
            },
          );

          const dayAppointments = filteredAppointments.filter(
            (appointment: any) => {
              const start = dayjs(appointment.startTimeUtc).startOf("day");
              const end = dayjs(appointment.startTimeUtc)
                .add(appointment.duration, "minutes")
                .startOf("day");

              const daysSpan = end.diff(start, "day");

              // only include true single-day appointments
              return start.isSame(cellDate, "day") && daysSpan <= 0;
            },
          );

          const dayTimeOffs = monthTimeOffsForDay({
            date: dayKey,
            timeoffs: timeoffs?.timeoffs ?? [],
            userIds: filterItems.users,
            resourceIds: filterItems.resources,
          });

          const isFullyAbsent = isDayFullyAbsent({
            date: dayKey,
            shifts: calendarData.allShifts ?? [],
            timeoffs: timeoffs?.timeoffs ?? [],
            userIds: filterItems.users,
          });

          const dayItems: DayItem[] = [
            ...dayAppointments.map((appointment: any) => ({
              kind: "appointment" as const,
              appointment,
              start: dayjs(appointment.startTimeUtc),
            })),
            ...dayTimeOffs.timed.map((entry) => ({
              kind: "timeOff" as const,
              entry,
              start: dayjs(entry.start),
            })),
          ].sort((a, b) => a.start.diff(b.start));

          // Create a separate copy of the occupied positions to avoid modifying the original
          const positionTracker = [...occupiedPositions];

          const positionedSingleDayItems: PositionedItem[] = [];

          // We need to assign positions one by one to properly track occupancy
          for (const item of dayItems) {
            // Find first available position
            const position = positionTracker.findIndex(
              (isOccupied) => !isOccupied,
            );

            // If we found an empty position and it's within MAX_POSITIONS, use it
            if (position !== -1 && position < MAX_POSITIONS) {
              // Mark this position as occupied
              positionTracker[position] = true;
              positionedSingleDayItems.push({
                item,
                position,
                isVisible: true,
              });
            } else {
              // Otherwise, this item will overflow
              positionedSingleDayItems.push({
                item,
                position: -1,
                isVisible: false,
              });
            }
          }

          const visibleSingleDayItems = positionedSingleDayItems
            .filter((positioned) => positioned.isVisible)
            .sort((a, b) => a.position - b.position);

          const overflowSingleDayItems = positionedSingleDayItems.flatMap(
            (positioned) => (positioned.isVisible ? [] : [positioned.item]),
          );

          const hasOverflow =
            multiDayCounts.overflow > 0 || overflowSingleDayItems.length > 0;

          const renderDayItem = (item: DayItem) =>
            item.kind === "appointment" ? (
              <MonthAppointmentBlock appointment={item.appointment} />
            ) : (
              <MonthTimeOffBlock
                timeOff={item.entry.timeOff}
                start={item.entry.start}
                date={dayKey}
              />
            );

          const dayItemKey = (item: DayItem) =>
            item.kind === "appointment"
              ? `a-${item.appointment.appointmentId}`
              : `t-${item.entry.timeOff.source}-${item.entry.timeOff.id}`;

          // Find multi-day segments for this cell and sort by position
          const cellMultiDaySegments = positionedSegments
            .filter(
              ({ blockStart, position }) =>
                blockStart.isSame(cellDate, "day") &&
                position !== undefined &&
                position < MAX_POSITIONS,
            )
            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0)); // Ensure position order

          const multiDayTopOffset = 0; // Starting Y position for multi-day appointments

          // Get overflow multi-day segments for this cell
          const overflowMultiDaySegments = positionedSegments.filter(
            ({ blockStart, position }) =>
              blockStart.isSame(cellDate, "day") &&
              (position === undefined || position >= MAX_POSITIONS),
          );

          return (
            <div className="relative" key={dayKey}>
              <MonthDayBlock
                disableOnHolidays={day.disableOnHolidays}
                isHoliday={day.isHoliday}
                isOutsideMonth={isOutsideMonth}
                isUnavailable={isFullyAbsent}
                badge={
                  dayTimeOffs.absences.length > 0 ? (
                    <MonthAbsenceBadge
                      absences={dayTimeOffs.absences}
                      date={dayKey}
                    />
                  ) : undefined
                }
                key={date}
                date={date}
                onDateClick={() =>
                  dateActions.setSelectedDateAndView(dayjs(date), "day")
                }
              >
                <div className="pt-8">
                  {/* Render multi-day appointments first (at the top) */}
                  {cellMultiDaySegments.map((segment) => {
                    const { appointment, blockStart, numDays, position } =
                      segment;

                    const allAppointmentSegments = positionedSegments.filter(
                      ({ appointment: a }) =>
                        a.appointmentId === appointment.appointmentId,
                    );

                    const isLastBlock =
                      allAppointmentSegments[
                        allAppointmentSegments.length - 1
                      ]?.blockStart.isSame(cellDate, "day") ?? false;

                    return (
                      <div
                        className="absolute left-0 w-full pl-1"
                        style={{
                          top: `${multiDayTopOffset + (position ?? 0) * 30}px`,
                          width: `calc(100% - 8px)`,
                          zIndex: 10 - (position ?? 0), // Higher positions (0, 1) get higher z-index
                        }}
                        key={`m-${appointment.appointmentId}-${blockStart.format("YYYY-MM-DD")}`}
                      >
                        <MonthAppointmentBlock
                          appointment={{
                            ...appointment,
                            multiDaySegmentStart: blockStart.toISOString(),
                          }}
                          multiDay={{
                            numDays,
                            isLastBlock,
                          }}
                        />
                      </div>
                    );
                  })}

                  {/* Render single-day items in their assigned positions */}
                  {visibleSingleDayItems.map(({ item, position }) => (
                    <div
                      key={dayItemKey(item)}
                      className="absolute left-0 w-full px-1"
                      style={{
                        top: `${multiDayTopOffset + (position + 1) * 30}px`,
                        zIndex: 1,
                      }}
                    >
                      {renderDayItem(item)}
                    </div>
                  ))}
                </div>

                {/* Render "show more" button if we have overflow appointments */}
                {hasOverflow && (
                  <Popover
                    placement="bottom"
                    className="max-w-[200px]"
                    showArrow
                  >
                    <PopoverTrigger>
                      <Button
                        size="sm"
                        variant="flat"
                        className="absolute top-[120px] left-1 z-0 h-6 justify-center rounded-sm text-center md:h-8 md:justify-start md:pl-2"
                        isIconOnly
                        style={{
                          width: `calc(100% - 8px)`,
                        }}
                      >
                        <span className="hidden md:block">
                          <Trans>Prikaži vse</Trans>
                        </span>
                        ...
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent>
                      <div className="flex w-full flex-col gap-1 py-2">
                        {/* Show overflow multi-day appointments first */}
                        {overflowMultiDaySegments.map(
                          ({ appointment, blockStart, numDays }) => {
                            const allAppointmentSegments =
                              positionedSegments.filter(
                                ({ appointment: a }) =>
                                  a.appointmentId === appointment.appointmentId,
                              );

                            const isLastBlock =
                              allAppointmentSegments[
                                allAppointmentSegments.length - 1
                              ]?.blockStart.isSame(cellDate, "day") ?? false;

                            return (
                              <MonthAppointmentBlock
                                key={`o-${appointment.appointmentId}-${blockStart.format("YYYY-MM-DD")}`}
                                appointment={{
                                  ...appointment,
                                  multiDaySegmentStart:
                                    blockStart.toISOString(),
                                }}
                                multiDay={{
                                  numDays,
                                  isLastBlock,
                                }}
                              />
                            );
                          },
                        )}

                        {/* Show overflow single-day items after multi-day ones */}
                        {overflowSingleDayItems.map((item) => (
                          <div key={dayItemKey(item)}>
                            {renderDayItem(item)}
                          </div>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </MonthDayBlock>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MonthCalendar;
